import { FormEvent, useEffect, useMemo, useState } from 'react';

import axios from 'axios';
import Slider from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import { Button } from 'react-bootstrap';

import { handleToast } from '../../../../../components/Toast';
import { ModalDesigner } from '../../../../../components/ModalDesigner';
import { financeRoutes } from '../../../../../utils/RoutesApi';

import { Container, ContentButton, ContentInput } from './styles';

const PrettoSlider = styled(Slider)({
  color: 'var(--primary)',
  height: 8,
  '& .MuiSlider-track': { border: 'none' },
  '& .MuiSlider-thumb': {
    height: 24,
    width: 24,
    backgroundColor: 'var(--primary)',
    color: 'var(--primary)',
    border: '2px solid currentColor',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&:before': { display: 'none' },
  },
  '& .MuiSlider-valueLabel': {
    lineHeight: 1.2,
    fontSize: 12,
    background: 'unset',
    padding: 0,
    width: 32,
    height: 32,
    borderRadius: '50% 50% 50% 0',
    backgroundColor: 'var(--primary)',
    transformOrigin: 'bottom left',
    transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
    '&:before': { display: 'none' },
    '&.MuiSlider-valueLabelOpen': {
      transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
    },
    '& > *': { transform: 'rotate(45deg)' },
  },
});

type RuleType =
  | 'action'
  | 'fii'
  | 'all'
  | 'broker'
  | 'stock'
  | 'reit'
  | 'etf';

interface IPropsException {
  key?: string;
  type?: string;
  corretora?: string;
  cod_acao?: string;
  tickerSymbol?: string;
  value?: number | string;
  PorcentagemAcoes?: number | string;
}

interface IPropsEditRule {
  Exception?: IPropsException[];
  ValorExp?: number | string;
  aportarQtde: number;
  closingPrice: number;
  corporationName: string;
  equitiesQuantity: number;
  novoupdateValue: number;
  percent: number;
  tickerSymbol: string;
  updateValue: number;

  productTypeName?: string;

  edit?: boolean;
  typeEdit: RuleType;
  brokerEdit?: string;
}

interface IPropsFormCreateRule {
  rule: IPropsEditRule;
  showModalCreateRule: boolean;
  setShowModalCreateRule: (value: boolean) => void;
  update: () => void | Promise<void>;
}

interface ISaveRuleResponse {
  message?: string;
  created?: boolean;
  data?: IPropsException;
}

function normalizeText(value?: string | null): string {
  return String(value ?? '').trim();
}

function normalizeTicker(value?: string | null): string {
  return normalizeText(value).toUpperCase();
}

function normalizeType(value?: string | null): RuleType | '' {
  const normalized = normalizeText(value).toLowerCase();

  const aliases: Record<string, RuleType> = {
    action: 'action',
    acao: 'action',
    ação: 'action',
    acoes: 'action',
    ações: 'action',
    fii: 'fii',
    all: 'all',
    broker: 'broker',
    stock: 'stock',
    stocks: 'stock',
    reit: 'reit',
    reits: 'reit',
    etf: 'etf',
    etfs: 'etf',
  };

  return aliases[normalized] ?? '';
}

function toValidNumber(value: unknown, fallback = 0): number {
  const normalizedValue =
    typeof value === 'string'
      ? value.replace(',', '.').trim()
      : value;

  const numberValue = Number(normalizedValue);

  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function getExceptionPercentage(exception?: IPropsException): number {
  if (!exception) return 0;

  if (exception.PorcentagemAcoes !== undefined) {
    return toValidNumber(exception.PorcentagemAcoes);
  }

  return toValidNumber(exception.value);
}

export function FormCreateRule({
  rule,
  showModalCreateRule,
  setShowModalCreateRule,
  update,
}: IPropsFormCreateRule) {
  const [percentInput, setPercentInput] = useState<string>('0');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const productType = normalizeType(rule.productTypeName);
  const typeEdit = normalizeType(rule.typeEdit);

  const selectedType =
    productType && productType !== 'all'
      ? productType
      : typeEdit;
  const selectedTicker = normalizeTicker(rule.tickerSymbol);
  const selectedBroker =
    selectedType === 'broker' ? normalizeText(rule.brokerEdit) : '';

  const currentException = useMemo(() => {
    const exceptions = rule.Exception ?? [];

    const exactMatch = exceptions.find((item) => {
      const itemType = normalizeType(item.type);
      const itemTicker = normalizeTicker(
        item.cod_acao ?? item.tickerSymbol
      );
      const itemBroker = normalizeText(item.corretora);

      if (itemType !== selectedType) return false;
      if (selectedType === 'broker') {
        return itemBroker === selectedBroker;
      }
      if (selectedType === 'all') return true;

      return Boolean(itemTicker) && itemTicker === selectedTicker;
    });

    if (exactMatch) return exactMatch;

    const sameTypeExceptions = exceptions.filter((item) => {
      const itemType = normalizeType(item.type);

      if (itemType !== selectedType) return false;

      if (selectedType === 'broker') {
        return normalizeText(item.corretora) === selectedBroker;
      }

      return true;
    });

    return sameTypeExceptions.length === 1
      ? sameTypeExceptions[0]
      : undefined;
  }, [rule.Exception, selectedType, selectedTicker, selectedBroker]);

  useEffect(() => {
    if (!showModalCreateRule) return;

    const savedPercentage = currentException
      ? getExceptionPercentage(currentException)
      : toValidNumber(rule.ValorExp);

    setPercentInput(String(savedPercentage));
  }, [
    showModalCreateRule,
    selectedTicker,
    selectedType,
    selectedBroker,
  ]);

  const numericPercent = toValidNumber(percentInput);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!selectedType) {
      handleToast(true, 'Não foi possível identificar o tipo da regra');
      return;
    }

    if (
      selectedType !== 'all' &&
      selectedType !== 'broker' &&
      !selectedTicker
    ) {
      handleToast(true, 'Não foi possível identificar o ativo');
      return;
    }

    if (selectedType === 'broker' && !selectedBroker) {
      handleToast(true, 'Não foi possível identificar a corretora');
      return;
    }

    const normalizedPercent = toValidNumber(percentInput, Number.NaN);

    if (!Number.isFinite(normalizedPercent)) {
      handleToast(true, 'Informe uma porcentagem válida');
      return;
    }

    if (normalizedPercent < 0 || normalizedPercent > 100) {
      handleToast(true, 'A porcentagem deve estar entre 0 e 100');
      return;
    }

    const payload = {
      PorcentagemAcoes: normalizedPercent,
      cod_acao: selectedTicker,
      tp_acao: '',
      type: selectedType,
      corretora: selectedBroker,
    };

    console.log('========== SALVAR REGRA ==========');
    console.log('rule completo:', rule);
    console.log('ticker normalizado:', selectedTicker);
    console.log('tipo normalizado:', selectedType);
    console.log('corretora normalizada:', selectedBroker);
    console.log('exceção localizada:', currentException);
    console.log('valor digitado:', percentInput);
    console.log('payload enviado:', payload);
    console.log(
      'URL:',
      `${process.env.REACT_APP_API_URL}${financeRoutes.FINANCE_EXCEPTION_CREATE}`
    );
    console.log('==================================');

    try {
      setIsSubmitting(true);

      const response = await axios.post<ISaveRuleResponse>(
        `${process.env.REACT_APP_API_URL}${financeRoutes.FINANCE_EXCEPTION_CREATE}`,
        payload
      );

      console.log('RESPOSTA DA API:', response.data);

      const returnedPercentage =
        response.data?.data?.PorcentagemAcoes;

      if (returnedPercentage !== undefined) {
        const savedValue = toValidNumber(returnedPercentage);
        console.log('PORCENTAGEM RETORNADA:', savedValue);
        setPercentInput(String(savedValue));
      }

      handleToast(
        false,
        response.data?.message ?? 'Regra salva com sucesso'
      );

      await Promise.resolve(update());
      setShowModalCreateRule(false);
    } catch (error: unknown) {
      const axiosError = axios.isAxiosError(error) ? error : null;

      console.error('ERRO COMPLETO:', error);
      console.error('RESPOSTA DO BACKEND:', axiosError?.response?.data);

      const backendMessage = (
        axiosError?.response?.data as { message?: string } | undefined
      )?.message;

      handleToast(
        true,
        backendMessage ??
          'Não foi possível salvar a regra. Tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDeleteRuleException() {
    const key = currentException?.key;

    if (!key) {
      handleToast(
        true,
        'Não foi possível identificar a regra para exclusão'
      );
      return;
    }

    try {
      setIsDeleting(true);

      await axios.delete(
        `${process.env.REACT_APP_API_URL}${financeRoutes.FINANCE_EXCEPTION_DELETE}`,
        { data: { key } }
      );

      handleToast(false, 'Regra excluída com sucesso');

      await Promise.resolve(update());
      setShowModalCreateRule(false);
    } catch (error: unknown) {
      const axiosError = axios.isAxiosError(error) ? error : null;

      console.error('ERRO AO EXCLUIR:', error);
      console.error('RESPOSTA DO BACKEND:', axiosError?.response?.data);

      const backendMessage = (
        axiosError?.response?.data as { message?: string } | undefined
      )?.message;

      handleToast(true, backendMessage ?? 'Erro ao excluir regra');
    } finally {
      setIsDeleting(false);
    }
  }

  const isLoading = isSubmitting || isDeleting;

  return (
    <ModalDesigner
      isOpen={showModalCreateRule}
      setState={setShowModalCreateRule}
      id="rule"
      title={
        currentException?.key
          ? 'Editar regra'
          : 'Cadastrar nova regra'
      }
    >
      <Container onSubmit={submit}>
        <ContentInput>
          <label>Porcentagem</label>

          <div>
            <PrettoSlider
              valueLabelDisplay="auto"
              aria-label="Porcentagem da regra"
              value={numericPercent}
              min={0}
              max={100}
              step={0.01}
              className="slider"
              disabled={isLoading}
              onChange={(_, value) => {
                const sliderValue = Array.isArray(value)
                  ? value[0]
                  : value;

                setPercentInput(String(sliderValue));
              }}
            />

            <input
              value={percentInput}
              placeholder="0"
              type="number"
              inputMode="decimal"
              min="0"
              max="100"
              step="0.01"
              disabled={isLoading}
              onChange={(event) => {
                setPercentInput(event.target.value);
              }}
              onBlur={() => {
                if (percentInput.trim() === '') {
                  setPercentInput('0');
                }
              }}
            />
          </div>
        </ContentInput>

        <ContentButton>
          {currentException?.key && (
            <Button
              variant="danger light"
              type="button"
              disabled={isLoading}
              onClick={handleDeleteRuleException}
            >
              {isDeleting ? 'Excluindo...' : 'Deletar regra'}
            </Button>
          )}

          <Button
            variant="primary"
            type="submit"
            disabled={isLoading}
          >
            {isSubmitting
              ? 'Salvando...'
              : currentException?.key
                ? 'Salvar alterações'
                : 'Enviar'}
          </Button>
        </ContentButton>
      </Container>
    </ModalDesigner>
  );
}